import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-instagram-kurti-scam-cyber-fraud-2026",
  title: "The ₹799 Kurti Scam That Broke Open a ₹30-Crore Fraud Network",
  description:
    "A Ramjas College student lost ₹6,450 to an Instagram kurti ad. Her complaint led Delhi Police to seven arrests and a ₹30-crore cyber fraud network. How the scam worked, and how to protect yourself.",
  pubDate: "2026-10-28",
  updatedDate: "2026-10-28",
  authorSlug: "dhairya-madaan",
  category: "news",
  tags: ["DU news", "cyber fraud", "Instagram scam", "online shopping safety", "student safety", "Delhi Police"],
  readMinutes: 8,
  heroEyebrow: "News · Delhi",
};

const src = (href: string, label: string) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

export function Body() {
  return (
    <>
      <figure className="my-8">
        <img
          src="/blog/du-instagram-kurti-scam-cyber-fraud-2026.jpg"
          alt="Illustration showing a shopping ad on a phone leading to a QR code payment, then to bank accounts, and finally to handcuffs."
          width={1200}
          height={688}
          fetchPriority="high"
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Illustration. Not a photo of the actual case.
        </figcaption>
      </figure>

      <div className="border border-rule bg-card p-5 my-6 text-sm">
        <strong className="mono-label text-grade">How we sourced this</strong>
        <p className="mt-2">
          The details of the case come from{" "}
          {src("https://indianexpress.com/article/delhi/delhi-university-du-student-fatima-instagram-kurti-scam-cyber-fraud-rs-30-crore-greater-noida-10938378/", "The Indian Express (27 October 2026)")},{" "}
          {src("https://www.ndtv.com/india-news/how-rs-799-kurti-order-led-delhi-police-to-rs-30-crore-cyber-fraud-network-12237406", "NDTV (26 October 2026)")},{" "}
          {src("https://www.hindustantimes.com/cities/delhi-news/7-held-for-supplying-bank-accounts-to-route-30cr-fraud-money-101793038065579.html", "Hindustan Times (27 October 2026)")},{" "}
          {src("https://timesofindia.indiatimes.com/city/delhi/rs-799-kurti-that-never-came-but-led-cops-to-rs-30-crore-cyber-frauds/articleshow/135214370.cms", "The Times of India (26 October 2026)")}, and{" "}
          {src("https://www.newindianexpress.com/cities/delhi/2026/Oct/27/shopping-fraud-complaint-leads-to-rs-30-crore-cyber-racket", "The New Indian Express (27 October 2026)")}
          . We are not naming the student. This is a report on a police case, not legal advice.
        </p>
      </div>

      <p>
        It started the way half the shopping on your feed starts. An ad. A nice kurti. A price that felt reasonable. It ended with seven arrests, three suspected shell firms and a police claim of over ₹30 crore routed through fake bank accounts. And the thread that connected all of it was one Delhi University student's complaint about ₹6,450.
      </p>
      <p>
        If you have ever bought anything from an Instagram page that does not have a real store, this story is worth ten minutes of your time. Not because it is dramatic, but because every step of the scam is a step you will recognise.
      </p>

      <h2>What happened, step by step</h2>
      <p>
        In June, a second-year student at Ramjas College, who is from Prayagraj, came across an advertisement for a kurti on an Instagram account named <strong>Kavya_kurti_hub</strong>. When she was ready to order, she was moved to WhatsApp, where she placed an order worth ₹799 and paid ₹400 in advance through a QR code.
      </p>
      <p>
        Then the pattern that should now be famous began. According to the Delhi Police, she got a call about her parcel and was asked for a passcode. When she contacted the seller, she was told to pay ₹1,150 as a refundable charge for generating the code, followed by payments of ₹2,000, ₹2,650 and ₹650 on similar pretexts. The parcel never came. The money never came back.
      </p>
      <p>
        In total, she lost ₹6,450 to a shop that did not exist.
      </p>

      <h2>The complaint that actually worked</h2>
      <p>
        Most people stop here. Embarrassed, annoyed, and out of pocket, they delete the chat and tell themselves to be more careful next time. This student did the boring thing instead. She filed a complaint on the National Cybercrime Reporting Portal, and an FIR was registered at the Cyber Police Station, North District, on 4 September.
      </p>
      <p>
        That matters, because the outcome here is unusual. The investigation, led by ACP Vishesh Dhatterwal and Additional DCP Ram Dulesh Meena under DCP (North) Niharika Bhatt, analysed nearly 200 mobile numbers and their IMEIs, then followed the money.
      </p>
      <p>
        The cheated amount was traced through a merchant account to a Canara Bank account of a firm called Kizentech Solutions Pvt Ltd in Greater Noida, linked to two of the accused. Coordinated raids followed in Greater Noida on 16 October, and a seventh arrest was made in Kanpur on 21 October. Police have linked 27 cyber fraud complaints from different states to this one network.
      </p>

      <h2>How a ₹6,450 scam connects to ₹30 crore</h2>
      <p>
        This is the part worth understanding, because it explains why your small loss is not actually small.
      </p>
      <ul>
        <li>
          Police say the syndicate set up three firms, Kizentech Solutions Pvt Ltd, Executive Edge and Nexcore, and opened bank accounts in their names and in the names of individuals.
        </li>
        <li>
          Four of the accused allegedly arranged these accounts, which were handed over to two others for a monthly payment of around ₹25,000, who then supplied them to cyber fraudsters.
        </li>
        <li>
          Hindustan Times reports that an MBA graduate, Gaurav Pandey, 24, allegedly coordinated the supply of bank accounts, while the others helped set up firms, arrange account holders and transfer banking kits.
        </li>
        <li>
          The Indian Express reported that all six accused shown in custody were well-educated, tech-savvy individuals. The seventh, Udit Pratap Singh Bhadauriya, is 22.
        </li>
      </ul>
      <p>
        In plain language: there is an industry of people whose entire job is to make stolen money hard to trace. They rent out bank accounts. When you pay a fake seller, your money lands in one of those rented accounts and is moved on within hours. The ₹30 crore figure is the volume police say passed through this arrangement.
      </p>
      <p>
        So when someone tells you that losing ₹800 to a fake shopping page is too little to bother reporting, this case is the answer. The complaint on the portal is the only reason any of this came to light.
      </p>

      <h2>The four red flags in this story</h2>
      <h3>1. The ad to WhatsApp jump</h3>
      <p>
        Legitimate shops take payment on the platform or on their own website, where there is some record and some protection. Being moved to WhatsApp takes the conversation to a place where nobody can audit it. That jump alone should make you stop.
      </p>
      <h3>2. Paying a personal QR code before delivery</h3>
      <p>
        A QR code sent in chat usually sends money straight into someone's account with no buyer protection and, in practice, no realistic way to reverse it. Advance payment to an unknown seller through a chat QR code is the single most common point where shopping scams begin.
      </p>
      <h3>3. The passcode call</h3>
      <p>
        No delivery company needs your UPI passcode, OTP or any code to deliver a parcel. Any call that asks for a code is a fraud call. In this case the passcode was the hook used to pull in the first extra payment.
      </p>
      <h3>4. Escalating charges after you have paid</h3>
      <p>
        Refundable charge. Code generation fee. Delivery processing fee. Customs clearance. The names change, the structure does not. A real seller charges you once, for the product. Every additional demand after payment is a signal that you are talking to a scammer, and the only winning move is to stop paying and start reporting.
      </p>

      <h2>If it happens to you</h2>
      <p>
        Do this in order, and do it fast, because the first hours matter most for freezing money.
      </p>
      <ol>
        <li>
          Call <strong>1930</strong>, the national cybercrime helpline, immediately. Have the transaction details ready: amount, time, UPI reference number, and the account or number you paid.
        </li>
        <li>
          File a complaint on the <strong>National Cybercrime Reporting Portal</strong> at cybercrime.gov.in. This is exactly what the Ramjas student did, and it is what started this investigation.
        </li>
        <li>
          Screenshot everything before you block anyone: the ad, the chat, the payment confirmations, the phone numbers. These are your evidence.
        </li>
        <li>
          Report the ad and the account on Instagram itself. Even if it does not get your money back, it slows the same page down for the next student.
        </li>
        <li>
          If your bank is involved, inform it in writing the same day and ask for the transaction to be flagged.
        </li>
      </ol>
      <p>
        Do not pay any more money, ever, in the hope of getting the earlier payments back. The refundable charge that unlocks your refund is the oldest line in the book, and it appears in this case too.
      </p>

      <h2>Before you order from that page</h2>
      <p>
        A thirty-second check filters out most of these shops. Look at how old the account is and whether the page has real, varied comments from actual buyers, not a wall of single-word praise. Search the page name with the word scam. Prefer cash on delivery for any seller you do not know, especially for a first order. Check whether the website, if there is one, has a working address and return policy rather than a WhatsApp number and vibes. And if the price looks too good against the same product elsewhere, treat that as information, not luck.
      </p>

      <h2>The bottom line</h2>
      <p>
        One student, one ₹799 order, one complaint on a government portal, and a network allegedly moving crores is now in police custody. The system is slow and far from perfect, but this case shows the report actually goes somewhere. Save 1930 in your phone today. You will not need it. That is the point.
      </p>
    </>
  );
}
